import { ColumnTypes, ITableColumn } from '@makyoapp/makyo-sdk/models/Table'
import { AspectRatio } from '@makyoapp/makyo-tw-ui'

import { IDBTable } from '@/core/db'
import { ICell } from '@/core/models/columns'
import { AttachmentCell } from '@/core/models/columns/attachment'
import { BooleanCell } from '@/core/models/columns/boolean'
import { DateTimeCell } from '@/core/models/columns/dateTime'
import { IFunctionCell } from '@/core/models/columns/function'
import { IconCell } from '@/core/models/columns/icon'
import { ImageCell } from '@/core/models/columns/image'
import { ImageArrayCell } from '@/core/models/columns/imageArray'
import { LookupCell } from '@/core/models/columns/lookup'
import { NumberCell } from '@/core/models/columns/number'
import { RatingCell } from '@/core/models/columns/rating'
import { ISingleCellCell, SingleCellCell } from '@/core/models/columns/single-cell'
import { URLCell } from '@/core/models/columns/url'
import { IRow } from '@/core/models/tables/row'

export type ColBrief = Pick<ITableColumn, '_id' | '_Type' | '_Settings'>

export function getColBrief(table?: IDBTable, colId?: string): ColBrief | undefined {
  if (!colId || !table) return
  if (colId === '_id') {
    return { _id: '_id', _Type: ColumnTypes.RowID, _Settings: {} }
  }

  let col = table.columnsMap.get(colId)
  if (!col) return
  return { _id: col._id, _Type: col._Type, _Settings: col._Settings }
}

export function getMediaColInfo(row: IRow, mediaCol?: ColBrief) {
  let value: string | undefined = undefined
  let type: string | undefined = undefined

  if (mediaCol && mediaCol._Type && mediaCol._id) {
    const mediaColValue = row[mediaCol._id]?.toString()

    switch (mediaCol._Type) {
      case ColumnTypes.Attachment:
        value = mediaColValue
        type = 'video'
        break
      case ColumnTypes.Icon:
        value = mediaColValue
        type = 'icon'
        break
      case ColumnTypes.Barcode:
      case ColumnTypes.QRCode:
      case ColumnTypes.URL:
      case ColumnTypes.Image:
        value = mediaColValue
        type = 'image'
        break
      case ColumnTypes.Lookup: {
        const cell = row[mediaCol._id] as LookupCell
        const cellValue = cell?.value as any
        const cellType = cell?.resolvedType

        value = Array.isArray(cellValue) ? cellValue[0]?.toString() : cell?.toDisplayString()

        if (cellType && cellType === ColumnTypes.Icon) {
          type = 'icon'
        } else if (cellType && cellType === ColumnTypes.Image) {
          type = 'image'
        }
        break
      }
      case ColumnTypes.SingleCell: {
        const cell = row[mediaCol._id] as ISingleCellCell
        const cellValue = cell?.value

        type = cell?.resolvedType
        value = Array.isArray(cellValue) ? cellValue[0]?.toString() : cellValue?.toString()

        break
      }
      case ColumnTypes.Function: {
        const cell = row[mediaCol._id] as IFunctionCell
        const cellValue = cell?.toReturnTypeCell()
        type = cell.returnType
        value = cellValue?.toString()
        break
      }
      default: {
        type = ColumnTypes.Text
        value = row[mediaCol._id]?.toString()
        break
      }
    }
  }

  return [value, type] as const
}

export const getSingleCellColType = (cell: ICell) => {
  const rawCells = (cell as ISingleCellCell)?.value

  switch (true) {
    case rawCells instanceof ImageArrayCell:
      return ColumnTypes.ImageArray
    case rawCells instanceof ImageCell:
      return ColumnTypes.Image
    case rawCells instanceof IconCell:
      return ColumnTypes.Icon
    case rawCells instanceof RatingCell:
      return ColumnTypes.Rating
    case rawCells instanceof AttachmentCell:
      return ColumnTypes.Attachment
    case rawCells instanceof NumberCell:
      return ColumnTypes.Number
    case rawCells instanceof URLCell:
      return ColumnTypes.URL
    case rawCells instanceof BooleanCell:
      return ColumnTypes.Boolean
    case rawCells instanceof DateTimeCell:
      return ColumnTypes.DateTime
    case rawCells instanceof LookupCell:
      return ColumnTypes.Lookup
    case rawCells instanceof SingleCellCell:
      return ColumnTypes.SingleCell
    default:
      return ColumnTypes.Text
  }
}

export function parseBooleanFromValue(value: unknown) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const normalizedValue = String(value).toLowerCase()
    return (
      ['true', '1', 'yes', 'y', 'on', 'enabled', 'checked'].includes(normalizedValue) &&
      !['false', '0', 'no', 'n', 'off', 'disabled', 'unchecked'].includes(normalizedValue)
    )
  }
  if (typeof value === 'number') return value === 1
  return false
}

export const convertRatioFormat = (ratio: string): AspectRatio => {
  if (!ratio || typeof ratio !== 'string') return ratio as AspectRatio

  if (ratio === 'auto') return 'auto' as AspectRatio

  return ratio.replace(':', '/') as AspectRatio
}
